import { InjectionToken } from '@angular/core';
import { IAnnualReport } from '@modeso/types__aumico-lib-annualreport-be';

/**
 * FX (REV-21512): the data contract between the app and the notes components.
 *
 * The app computes everything with `FxTranslationHelper` (the 03b engine) and passes plain data
 * down — the lib never imports the engine (GroupingHelper pattern: the app calculates and stamps,
 * the lib reads). Present only on FX reports (`setupData.fxEnabled`); `null`/absent otherwise.
 *
 * Per-node translated values are NOT in here: the app stamps them as `__fx` onto the template
 * items the selection trees are built from (see IFxNodeStamp) — exactly how `closingReClass`
 * reaches the super table today.
 */
export interface IFxNotesContext {
    /** Functional currency name (e.g. USD) — same as annualReportDefaultSettings.currency. */
    functionalCurrency: string;
    /** Display currency name (e.g. CHF). */
    displayCurrency: string;
    /** Period rates as entered in rate setup; null while missing. */
    closingRateCY: number | null;
    closingRatePY: number | null;
    averageRateCY: number | null;
    averageRatePY: number | null;
    /** The translation reserve per year, exactly as the Kontrolle tab (03d) computes it. */
    reserveCY: number | null;
    reservePY: number | null;
    /** Translated label for the virtual reserve node in the item picker (the app owns i18n). */
    reserveLabel: string;
}

/** The FLI component's tri-state currency display, cycled by the chip. */
export type FxCurrencyDisplay = 'functional' | 'both' | 'display';

/**
 * The ONE rule for the chip's effective state: non-FX reports are always functional; on FX
 * reports an unset option means the FX default «both». Used by the editor, the chip label and
 * both print mappers — change it here or nowhere.
 */
export function effectiveFxCurrencyDisplay(
    options: { currencyDisplay?: FxCurrencyDisplay } | null | undefined,
    fxEnabled: boolean,
): FxCurrencyDisplay {
    if (!fxEnabled) {
        return 'functional';
    }

    return options?.currencyDisplay ?? 'both';
}

/**
 * The stamp-map key shared by the FlisMapper (writer) and every FLI display/print reader.
 * NOTE: `accountName` is user-editable in the note — a rename breaks the lookup by design
 * (the cell goes empty instead of showing a stale value).
 *
 * A balance-switch account's two legs share id AND name; only the leg (`originsFromAsset`, carried
 * on the row since the FlisMapper writes it) tells them apart. A row without it keeps the plain key.
 */
export function fliFxStampKeyOf(account: {
    id?: unknown;
    accountName?: string | null;
    originsFromAsset?: boolean | null;
}): string {
    const key = `${account.id}|${account.accountName}`;

    return typeof account.originsFromAsset === 'boolean'
        ? `${key}|${account.originsFromAsset ? 'asset' : 'liability'}`
        : key;
}

/** Sentinel identity of the virtual reserve node — stable across copies (G1). */
export const FX_RESERVE_NODE_NAME = '__fx-translation-reserve__';

/**
 * FX (REV-21512): the FX bundle the super-table print preview needs to render the CY_FX/PV_FX
 * twin columns. Same division of labour as the editor: the app builds the context AND a stamping
 * closure over its 03b lookups (`FxTranslationHelper` stays app-side); the display component
 * applies the closure to its own reclass-applied template clones before building the live trees,
 * then only reads `__fx`.
 */
export interface IFxSuperTablePrintContext {
    context: IFxNotesContext;
    /** Stamps `__fx` onto every node of a (cloned) template group of the given statement. */
    stampTemplateGroup: (templateGroup: unknown, statement: 'balance' | 'income') => void;
}

/**
 * FX (REV-21512): the app-provided FX engine facade for the notes display components — the same
 * self-service pattern as `applyReclassificationsToFinStatements`: the component already holds
 * the annualReport (store subscription) and derives everything itself; only the ENGINE lives
 * app-side, so the app provides this token and the lib injects it optionally. Absent provider
 * (tests, storybook, non-report apps) means no FX — exact legacy rendering (G0).
 */
export interface IFxNotesPrintEngine {
    /** Builds the print-side FX bundle from the given annualReport — null on non-FX reports. */
    buildSuperTablePrintFx(annualReport: IAnnualReport): IFxSuperTablePrintContext | null;
}

export const FX_NOTES_PRINT_ENGINE = new InjectionToken<IFxNotesPrintEngine>('aumico.fxNotesPrintEngine');

/**
 * The `__fx` stamp the app attaches to template items/accounts before they enter the notes
 * (selection trees, FLI walks). Values come from the decorated 03b view models, matched by the
 * same identities the engine uses (FLI/group name, account identifier).
 */
export interface IFxNodeStamp {
    displayCY: number | null;
    displayPY: number | null;
    functionalCY: number;
    functionalPY: number;
}
